'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { ChevronDown, Menu, MessageCircle, X } from 'lucide-react';
import { LogoutButton } from '@/components/auth/LogoutButton';
import { Button } from '@/components/ui/Button';
import { ThemeToggle } from '@/components/layout/ThemeToggle';
import { getValidSession } from '@/lib/auth/auth-service';
import { AUTH_EVENTS, AUTH_STORAGE_KEYS } from '@/lib/auth/constants';
import { WHATSAPP_URL } from '@/lib/whatsapp';
import { featuredSolution, solutions, type SolutionItem } from '@/content/solutions';
import { cn } from '@/lib/utils';

const landingMenuLinks = [
  { href: '/#como-funciona', label: 'Cómo funciona' },
  { href: '/funciones/', label: 'Funciones' },
  { href: '/precios/', label: 'Precios' },
  { href: '/blog/', label: 'Recursos' },
];

interface SolutionMenuItemProps {
  solution: SolutionItem;
  onSelect?: () => void;
  compact?: boolean;
}

function SolutionMenuItem({ solution, onSelect, compact = false }: SolutionMenuItemProps) {
  const SolutionIcon = solution.icon;
  const isSoon = solution.status === 'soon';
  const content = (
    <>
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-button border border-border bg-bg-secondary text-accent',
          compact ? 'h-10 w-10' : 'h-11 w-11'
        )}
      >
        <SolutionIcon size={compact ? 18 : 20} aria-hidden="true" />
      </span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-heading text-sm font-semibold text-text-primary">{solution.label}</span>
          {isSoon ? (
            <span className="shrink-0 rounded-chip border border-accent/30 bg-accent/10 px-2 py-0.5 text-[0.62rem] font-semibold text-accent">
              Próximamente
            </span>
          ) : null}
        </span>
        <span className="mt-1 block text-xs leading-5 text-text-secondary">{solution.description}</span>
      </span>
    </>
  );

  const className = cn(
    'flex min-w-0 gap-3 rounded-card p-3 text-left transition-colors',
    isSoon ? 'cursor-default opacity-72 sm:col-span-2' : 'hover:bg-bg-secondary'
  );

  if (!solution.href || isSoon) {
    return (
      <div className={className} aria-disabled="true">
        {content}
      </div>
    );
  }

  return (
    <Link href={solution.href} className={className} onClick={onSelect}>
      {content}
    </Link>
  );
}

export function Header() {
  const pathname = usePathname();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [isMobileSolutionsOpen, setIsMobileSolutionsOpen] = useState(false);
  const solutionsMenuRef = useRef<HTMLDivElement>(null);
  const FeaturedIcon = featuredSolution.icon;

  useEffect(() => {
    let isMounted = true;

    const syncSession = () => {
      void getValidSession().then((session) => {
        if (!isMounted) {
          return;
        }

        setIsAuthenticated(Boolean(session));
        setIsReady(true);
      });
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key === AUTH_STORAGE_KEYS.session || event.key === null) {
        syncSession();
      }
    };

    syncSession();
    window.addEventListener('storage', onStorage);
    window.addEventListener(AUTH_EVENTS.sessionChanged, syncSession);
    window.addEventListener('focus', syncSession);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(AUTH_EVENTS.sessionChanged, syncSession);
      window.removeEventListener('focus', syncSession);
    };
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileSolutionsOpen(false);
    setIsSolutionsOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen && !isSolutionsOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
        setIsMobileSolutionsOpen(false);
        setIsSolutionsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen, isSolutionsOpen]);

  useEffect(() => {
    if (!isSolutionsOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!solutionsMenuRef.current?.contains(event.target as Node)) {
        setIsSolutionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [isSolutionsOpen]);

  const isDashboardRoute = pathname?.startsWith('/dashboard') ?? false;
  const showLandingMenu = !isDashboardRoute;

  if (isDashboardRoute) {
    return null;
  }

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileSolutionsOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border bg-bg-primary/95 backdrop-blur-xl">
      <div className="container mx-auto flex h-20 items-center justify-between gap-3 px-6 md:h-16">
        <Link href="/" className="flex shrink-0 items-center space-x-2" onClick={closeMobileMenu}>
          <span className="text-[2rem] font-bold leading-none text-accent md:text-xl">OficioYa</span>
        </Link>

        <nav className="hidden items-center space-x-6 lg:flex" aria-label="Menú principal">
          {showLandingMenu ? (
            <>
              <Link href={landingMenuLinks[0].href} className="text-sm text-text-secondary transition-colors hover:text-accent">
                {landingMenuLinks[0].label}
              </Link>

              <div ref={solutionsMenuRef} className="relative">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-sm text-text-secondary transition-colors hover:text-accent"
                  aria-expanded={isSolutionsOpen}
                  aria-haspopup="menu"
                  onClick={() => setIsSolutionsOpen((isOpen) => !isOpen)}
                >
                  Soluciones
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className={cn('transition-transform', isSolutionsOpen ? 'rotate-180' : '')}
                  />
                </button>

                {isSolutionsOpen ? (
                  <div
                    className="fixed inset-x-4 top-20 z-50 mx-auto max-h-[calc(100dvh-6rem)] w-auto max-w-[58rem] overflow-x-hidden overflow-y-auto overscroll-contain rounded-card border border-border bg-bg-primary shadow-[0_28px_80px_rgb(0_0_0/0.18)]"
                    role="menu"
                  >
                    <div className="grid lg:grid-cols-[1fr_18rem]">
                      <div className="grid gap-1 p-4 sm:grid-cols-2">
                        {solutions.map((solution) => (
                          <SolutionMenuItem
                            key={solution.slug}
                            solution={solution}
                            onSelect={() => setIsSolutionsOpen(false)}
                          />
                        ))}
                      </div>
                      <div className="border-t border-border bg-bg-secondary p-5 lg:border-l lg:border-t-0">
                        <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-button bg-accent text-bg-primary">
                          <FeaturedIcon size={21} aria-hidden="true" />
                        </div>
                        <p className="font-heading text-lg font-semibold leading-tight text-text-primary">
                          {featuredSolution.title}
                        </p>
                        <p className="mt-3 text-sm leading-6 text-text-secondary">{featuredSolution.description}</p>
                        <Link
                          href="/#hero"
                          className="mt-5 inline-flex items-center gap-2 rounded-button border border-border bg-bg-primary px-4 py-2 font-heading text-sm font-semibold text-text-primary transition-colors hover:border-accent hover:text-accent"
                          onClick={() => setIsSolutionsOpen(false)}
                        >
                          Empezar gratis
                        </Link>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {landingMenuLinks.slice(1).map((link) => (
                <Link key={link.href} href={link.href} className="text-sm text-text-secondary transition-colors hover:text-accent">
                  {link.label}
                </Link>
              ))}
            </>
          ) : null}
        </nav>

        <div className="flex shrink-0 items-center space-x-2 sm:space-x-4">
          {showLandingMenu ? (
            <button
              type="button"
              className="touch-target inline-flex items-center justify-center rounded-button text-accent lg:hidden"
              aria-label={isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-controls="landing-mobile-menu"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen((isOpen) => !isOpen)}
            >
              {isMobileMenuOpen ? (
                <X className="pointer-events-none" size={34} strokeWidth={2.4} aria-hidden="true" />
              ) : (
                <Menu className="pointer-events-none" size={34} strokeWidth={2.4} aria-hidden="true" />
              )}
            </button>
          ) : null}

          {isReady ? (
            isAuthenticated ? (
              <LogoutButton
                className="hidden px-0 py-0 text-sm text-text-secondary hover:text-accent lg:inline-flex"
                size="sm"
                variant="ghost"
              />
            ) : (
              <>
                <Link href="/login/" className="hidden text-sm text-text-secondary transition-colors hover:text-accent lg:inline">
                  Ingresar
                </Link>
              </>
            )
          ) : null}

          {showLandingMenu ? (
            <Button
              size="sm"
              className={isAuthenticated ? 'hidden px-3 lg:inline-flex lg:px-4' : 'hidden px-0 lg:inline-flex lg:w-auto lg:px-4'}
              asChild
            >
              {isAuthenticated ? (
                <Link href="/dashboard/">Dashboard</Link>
              ) : (
                  <Link href="/registro/">Registrarse</Link>
              )}
            </Button>
          ) : null}

          <ThemeToggle />
        </div>
      </div>

      {showLandingMenu ? (
        <div
          id="landing-mobile-menu"
          className={isMobileMenuOpen ? 'border-t border-border bg-bg-primary/98 lg:hidden' : 'hidden'}
        >
          <nav className="container mx-auto grid gap-1 px-6 py-4" aria-label="Menú principal móvil">
            <Link
              href={landingMenuLinks[0].href}
              className="rounded-button px-1 py-3 font-heading text-lg font-semibold text-text-primary transition-colors hover:text-accent"
              onClick={closeMobileMenu}
            >
              {landingMenuLinks[0].label}
            </Link>

            <button
              type="button"
              className="flex items-center justify-between rounded-button px-1 py-3 text-left font-heading text-lg font-semibold text-text-primary transition-colors hover:text-accent"
              aria-expanded={isMobileSolutionsOpen}
              onClick={() => setIsMobileSolutionsOpen((isOpen) => !isOpen)}
            >
              Soluciones
              <ChevronDown
                size={20}
                aria-hidden="true"
                className={cn('transition-transform', isMobileSolutionsOpen ? 'rotate-180' : '')}
              />
            </button>

            {isMobileSolutionsOpen ? (
              <div className="grid gap-2 rounded-card border border-border bg-bg-secondary/65 p-2">
                {solutions.map((solution) => (
                  <SolutionMenuItem key={solution.slug} solution={solution} compact onSelect={closeMobileMenu} />
                ))}
              </div>
            ) : null}

            {landingMenuLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="rounded-button px-1 py-3 font-heading text-lg font-semibold text-text-primary transition-colors hover:text-accent"
                onClick={closeMobileMenu}
              >
                {link.label}
              </Link>
            ))}

            <div className="mt-3 grid gap-3 border-t border-border pt-4">
              {isReady && isAuthenticated ? (
                <>
                  <Link
                    href="/dashboard/"
                    className="rounded-button px-1 py-3 font-heading text-lg font-semibold text-text-primary transition-colors hover:text-accent"
                    onClick={closeMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <LogoutButton className="w-full justify-center" size="full" variant="secondary" />
                </>
              ) : (
                <>
                  {isReady ? (
                    <>
                      <Link
                        href="/login/"
                        className="rounded-button px-1 py-3 font-heading text-lg font-semibold text-text-primary transition-colors hover:text-accent"
                        onClick={closeMobileMenu}
                      >
                        Ingresar
                      </Link>
                      <Button size="full" variant="secondary" asChild>
                        <Link href="/registro/" onClick={closeMobileMenu}>
                          Registrar Negocio
                        </Link>
                      </Button>
                    </>
                  ) : null}
                  {/* <Button size="full" asChild>
                    <a
                      href={WHATSAPP_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-track-event="whatsapp_cta_click"
                      data-cta-location="unknown"
                      data-cta-text="Automatizar agenda"
                      onClick={closeMobileMenu}
                    >
                      <MessageCircle size={20} aria-hidden="true" />
                      Automatizar agenda
                    </a>
                  </Button> */}
                </>
              )}
            </div>
          </nav>
        </div>
      ) : null}
    </header>
  );
}
